import { GiftIcon, LayersIcon, LayoutGridIcon, LucideIcon, PackageIcon } from 'lucide-react';

import {
  BillStatus,
  InvoiceStatus,
  LocationStatus,
  LocationType,
  ServiceAgreementStatus,
} from '@invenco/accounts-interface';
import {
  ChildConditionType,
  ConditionOperator,
  OrderHoldSubtype,
  OrderHoldType,
  PropertyConditionName,
  WorkflowDataType,
  WorkflowStatus,
  WorkflowTriggerType,
} from '@invenco/common-domain/enums';
import {
  ShippingMethodAutoInvite,
  ShippingMethodRateCalculationMethod,
  ShippingMethodRateMatchMethod,
  ShippingMethodStatus,
} from '@invenco/common-domain/enums/ShippingMethod';
import { AddressType } from '@invenco/common-interface/shared';
import { LocationInventoryStatus } from '@invenco/inventory-interface';
import {
  FulfillmentStrategy,
  SkuSelectionMethod,
  SkuType,
  SkuUnitOfMeasure,
  SkuUnitType,
} from '@invenco/products-interface';
import {
  FulfilmentServiceType,
  ListingStatus,
  ListingStrategy,
  OrderAction,
  OrderLineStatus,
  OrderStatus,
} from '@invenco/sales-interface/interfaces';
import {
  CarrierServiceCategory,
  CarrierServiceRateMatchMethod,
  CostingStrategy,
  ShipmentClassification,
  ShipmentStatus,
  ShipmentTrackingStatus,
} from '@invenco/shipping-interface';
import { AsnStatus, ReturnCategory, ReturnStatus } from '@invenco/supply-interface';

import type {
  SimulationInputsStatus,
  SimulationScenarioRunStatus,
} from '@/gateways/impl/SimulationGateway';

import { EnumMap } from './types';

export const asnStatusTitle: EnumMap<AsnStatus> = {
  DRAFT: 'Draft',
  PENDING: 'Pending',
  OPEN: 'Booked',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};
export const returnStatusTitle: EnumMap<ReturnStatus> = {
  PENDING: 'Pending',
  OPEN: 'Open',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};
export const returnCategoryTitle: EnumMap<ReturnCategory> = {
  CUSTOMER_RETURN: 'Customer return',
  RETURN_TO_SENDER: 'Return to sender',
  REPLY_PAID: 'Reply paid',
  CANCELLED: 'Cancelled',
  UNKNOWN: 'Unknown',
};
export const orderStatusTitle: EnumMap<OrderStatus> = {
  draft: 'Draft',
  pending: 'Pending',
  hold: 'Hold',
  open: 'Open',
  closed: 'Closed',
  cancelled: 'Cancelled',
};
export const orderLineStatusTitle: EnumMap<OrderLineStatus> = {
  BACKORDER: 'Backorder',
  CLOSED: 'Closed',
  ITEM_NOT_FOUND: 'Invalid SKU',
  OPEN: 'Open',
  PARTIAL: 'Partial',
  PENDING: 'Pending',
};
export const orderActionTitle: EnumMap<OrderAction> = {
  APPLY_MANUAL_HOLD: 'Hold',
  CANCEL: 'Cancel Order',
  CLOSE: 'Close Order',
  RELEASE: 'Release',
  RELEASE_MANUAL_HOLD: 'Release hold',
  REVERT_TO_DRAFT: 'Edit',
};
export const shipmentStatusTitle: EnumMap<ShipmentStatus> = {
  DRAFT: 'Draft',
  WAITING: 'Waiting',
  PROCESSING: 'Processing',
  SHIPPED: 'Shipped',
  CANCELLED: 'Cancelled',
};
export const shipmentTrackingStatusTitle: EnumMap<ShipmentTrackingStatus> = {
  [ShipmentTrackingStatus.PENDING]: 'Pending',
  [ShipmentTrackingStatus.IN_TRANSIT]: 'In transit',
  [ShipmentTrackingStatus.DELIVERED]: 'Delivered',
};
export const fulfilmentServiceTypeTitle: EnumMap<FulfilmentServiceType> = {
  ECONOMY: 'Economy',
  STANDARD: 'Standard',
  PRIORITY: 'Priority',
};
export const simulationInputsStatusTitle: EnumMap<SimulationInputsStatus> = {
  DRAFT: 'Draft',
  UPLOADED: 'Uploaded',
  INGESTING: 'Ingesting',
  READY: 'Ready',
  FAILED: 'Failed',
};
export const simulationScenarioRunStatusTitle: EnumMap<SimulationScenarioRunStatus> = {
  PENDING: 'Pending',
  RUNNING: 'Running',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
};
export const skuTypeTitle: EnumMap<SkuType> = {
  STANDARD: 'Standard',
  BUNDLE: 'Bundle',
  MYSTERY: 'Mystery',
  ALIAS: 'Alias',
};
export const skuTypeIcon: EnumMap<SkuType, LucideIcon> = {
  STANDARD: PackageIcon,
  BUNDLE: LayoutGridIcon,
  MYSTERY: GiftIcon,
  ALIAS: LayersIcon,
};
export const skuUnitTypeTitle: EnumMap<SkuUnitType> = {
  BASE: 'Base',
  TRADE: 'Trade',
};
export const unitOfMeasureTitle: EnumMap<SkuUnitOfMeasure> = {
  EACH: 'Each',
  CARTON: 'Carton',
  PALLET: 'Pallet',
};

export const fulfillmentStrategyTitle: EnumMap<FulfillmentStrategy> = {
  STOCK: 'Stock',
  BUNDLE: 'Bundle',
};
export const orderHoldTypeTitle: EnumMap<OrderHoldType> = {
  AUTOMATION_PROCESS: 'Automation',
  RESERVE_INVENTORY_PROCESS: 'Backorder',
  SHIPPING_ADDRESS_PROCESS: 'Invalid Address',
  MANUAL_HOLD: 'Manual Hold',
  ITEM_NOT_FOUND: 'Invalid SKU',
  SHIPPING_HOLD: 'Shipping Hold',
};
export const orderHoldSubtypeTitle: EnumMap<OrderHoldSubtype> = {
  NO_PREFERRED_RATE: 'No Preferred Rate',
  SHIPPING_PROFILE_NOT_FOUND: 'Shipping Profile Not Found',
  MISSING_CUSTOMS_DATA: 'Missing Customs Data',
};

export const listingStatusTitle: EnumMap<ListingStatus> = {
  ACTIVE: 'Active',
  DRAFT: 'Draft',
  ARCHIVED: 'Archived',
};
export const listingStrategyTitle: EnumMap<ListingStrategy> = {
  PRESELL: 'Presell',
  DYNAMIC_AVAILABLE: 'Dynamic',
  SOLD_OUT: 'Sold out',
  STATIC_RINGFENCE: 'Static',
};
export const locationInventoryStatusTitle: EnumMap<LocationInventoryStatus> = {
  in_stock: 'In Stock',
  out_of_stock: 'Out of Stock',
};
export const carrierServiceCategoryTitle: EnumMap<CarrierServiceCategory> = {
  STANDARD: 'Standard',
  EXPRESS: 'Express',
  PICK_UP: 'Pick Up',
  SAME_DAY: 'Same Day',
};
export const carrierServiceRateMatchMethodTitle: EnumMap<CarrierServiceRateMatchMethod> = {
  ALL: 'All',
  STATE_STATE: 'By State',
  COUNTRY_COUNTRY: 'By Country',
  TO_POSTCODE_RANGE: 'To Postcode Range',
  TO_POSTCODE: 'To Postcode',
  TO_LOCALITY: 'To Locality',
};
export const shippingMethodRateMatchMethodTitle: EnumMap<ShippingMethodRateMatchMethod> = {
  ALL: 'All',
  STATE_STATE: 'By State',
  COUNTRY_COUNTRY: 'By Country',
  TO_POSTCODE_RANGE: 'To Postcode Range',
  TO_POSTCODE: 'To Postcode',
  TO_LOCALITY: 'To Locality',
};
export const shippingMethodAutoInviteTitle: EnumMap<ShippingMethodAutoInvite> = {
  NONE: 'None',
  MANAGED_LOCATIONS: 'Managed Locations',
};
export const shippingMethodRateCalculationMethodTitle: EnumMap<ShippingMethodRateCalculationMethod> =
  {
    PER_CONSIGNMENT: 'Per Consignment',
    PER_PACKAGE: 'Per Package',
  };
export const shipmentClassificationTitle: EnumMap<ShipmentClassification> = {
  DOMESTIC: 'Domestic',
  INTERNATIONAL: 'International',
};
export const costingStrategyTitle: EnumMap<CostingStrategy> = {
  QUOTE: 'Quote',
  NONE: 'None',
};

export const locationStatusTitle: EnumMap<LocationStatus> = {
  ACTIVE: 'Active',
  ARCHIVED: 'Archived',
};

export const workflowStatusTitle: EnumMap<WorkflowStatus> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};
export const workflowDataTypeTitle: EnumMap<WorkflowDataType> = {
  ORDER: 'Order',
  WAREHOUSE_SHIPMENT: 'Warehouse Shipment',
};
export const workflowTriggerTypeTitle: EnumMap<WorkflowTriggerType> = {
  ORDER_RELEASED: 'Order Released',
  WAREHOUSE_SHIPMENT_EXPORT: 'Warehouse Shipment Exported',
};
export const propertyConditionNameTitle: EnumMap<PropertyConditionName> = {
  channelName: 'Channel',
  ORDER_CHANNEL_NAME: 'Channel (multiple)',
  channelCategory: 'Channel Category',
  channelBrandName: 'Channel Brand Name',
  countryCode: 'Country Code',
  ORDER_COUNTRY_CODE: 'Country Code (multiple)',
  currency: 'Currency',
  orderedAt: 'Ordered At',
  skuName: 'SKUs',
  totalItems: 'Total Items',
  totalPrice: 'Total Price',
  totalVolume: 'Total Volume',
  ORDER_TOTAL_WEIGHT: 'Total Weight',
  email: 'Email',
  customerName: 'Customer Name',
  customerOrderNumber: 'Customer Order Number',
  shippingAddressCustomerName: 'Shipping Address Customer Name',
  ORDER_SHIPPING_ADDRESS_CUSTOMER_NAME: 'Shipping Address Customer Name (multiple)',
  shippingAddressEmail: 'Shipping Address Email',
  ORDER_SHIPPING_ADDRESS_EMAIL: 'Shipping Address Email (multiple)',
  shippingAddressState: 'Shipping Address State',
  ORDER_SHIPPING_ADDRESS_STATE: 'Shipping Address State (multiple)',
  ORDER_SHIPPING_ADDRESS_POSTCODE: 'Shipping Address Postcode',
  ORDER_SHIPPING_ADDRESS_POSTCODE_MULTIPLE: 'Shipping Address Postcode (multiple)',
  billingAddressCustomerName: 'Billing Address Customer Name',
  billingAddressEmail: 'Billing Address Email',
  ORDER_SHIPPING_PROFILE_ID: 'Shipping Profile',
  tagName: 'Tags',
  ORDER_EMAIL: 'Email (multiple)',
  ORDER_CUSTOMER_NAME: 'Customer Name (multiple)',
  ORDER_ORDER_LINE_ALL_SKUS: 'All SKUs',
  ORDER_ORDER_LINE_NO_SKUS: 'No SKUs',
  ORDER_ORDER_LINE_ANY_SKUS: 'At Least One SKU',
  ORDER_ORDER_LINE_UNIQUE_SKU_COUNT: 'Unique SKU Count',
  WAREHOUSE_SHIPMENT_WAREHOUSE_CODE: 'Shipment Warehouse Code',
  WAREHOUSE_SHIPMENT_TENANT_CODE: 'Shipment Tenant Code',
  WAREHOUSE_SHIPMENT_CHANNEL_NAME: 'Channel Name',
  WAREHOUSE_SHIPMENT_CHANNEL_CATEGORY: 'Channel Category',
  WAREHOUSE_SHIPMENT_CHANNEL_BRAND: 'Channel Brand',
  WAREHOUSE_SHIPMENT_FULFILLMENT_SERVICE_TYPE: 'Fulfillment Service Type',
  WAREHOUSE_SHIPMENT_TOTAL_ITEMS: 'Total Items',
  WAREHOUSE_SHIPMENT_TOTAL_WEIGHT: 'Total Weight',
  WAREHOUSE_SHIPMENT_TOTAL_VOLUME: 'Total Volume',
  WAREHOUSE_SHIPMENT_CARRIER_SERVICE: 'Carrier Service',
  WAREHOUSE_SHIPMENT_SHIPPING_ADDRESS_COUNTRY: 'Shipment Country',
  WAREHOUSE_SHIPMENT_CREATED_AT: 'Shipment Created At',
  WAREHOUSE_SHIPMENT_FULFILLMENT_ALL_SKUS: 'All SKUs',
  WAREHOUSE_SHIPMENT_FULFILLMENT_NO_SKUS: 'No SKUs',
  WAREHOUSE_SHIPMENT_FULFILLMENT_ANY_SKUS: 'At Least One SKU',
  ORDER_NAME: 'Order Name',
  ORDER_CUSTOM_ATTRIBUTE: 'Order Custom Attribute',
};
export const conditionOperatorTitle: EnumMap<ConditionOperator> = {
  CONTAINS: 'contains',
  DOES_NOT_CONTAIN: 'does not contain',
  EQUAL_TO: 'is',
  GREATER_THAN: 'is greater than',
  GREATER_THAN_OR_EQUAL_TO: 'is greater than or equal to',
  IN: 'is in',
  ALL_IN_GROUP: 'in group', // All item is "in group"
  NONE_IN_GROUP: 'in group', // None item is "in group"
  SOME_IN_GROUP: 'in group', // Some item is "in group"
  LESS_THAN: 'is less than',
  LESS_THAN_OR_EQUAL_TO: 'is less than or equal to',
  NOT_EQUAL_TO: 'is not',
  NOT_IN: 'is not in',
  ALL_NOT_IN_GROUP: 'not in group', // All item is "not in group"
  NONE_NOT_IN_GROUP: 'not in group', // None item is "not in group"
  SOME_NOT_IN_GROUP: 'not in group', // Some item is "not in group"
  STARTS_WITH: 'starts with',
  ENDS_WITH: 'ends with',
  STRING_CONTAINS: 'contains',
  STRING_DOES_NOT_CONTAIN: 'does not contain',
  EQUAL_TO_CASE_INSENSITIVE: 'is (case insensitive)',
  NOT_EQUAL_TO_CASE_INSENSITIVE: 'is not (case insensitive)',
  IN_CASE_INSENSITIVE: 'is in (case insensitive)',
  NOT_IN_CASE_INSENSITIVE: 'is not in (case insensitive)',
  CONTAINS_CASE_INSENSITIVE: 'contains (case insensitive)',
  DOES_NOT_CONTAIN_CASE_INSENSITIVE: 'does not contain (case insensitive)',
  IS_EMPTY: 'is empty',
  IS_NOT_EMPTY: 'is not empty',
};
export const childConditionTypeTitle: EnumMap<ChildConditionType> = {
  ALL: 'AND',
  ANY: 'OR',
};

export const billStatusTitle: EnumMap<BillStatus> = {
  PENDING: 'Pending',
  OPEN: 'Open',
  CLOSED: 'Closed',
};
export const invoiceStatusTitle: EnumMap<InvoiceStatus> = {
  PENDING: 'Pending',
  OPEN: 'Open',
};

export const serviceAgreementStatusTitle: EnumMap<ServiceAgreementStatus> = {
  ACTIVE: 'Active',
  TERMINATED: 'Terminated',
};

export const addressTypeTitle: EnumMap<AddressType> = {
  RESIDENTIAL: 'Residential',
  POSTAL: 'Postal',
  BUSINESS: 'Business',
};

export const skuSelectionMethodTitle: EnumMap<SkuSelectionMethod> = {
  LEAST_AVAILABLE: 'Least Available',
  MOST_AVAILABLE: 'Most Available',
  MOST_UNIQUE_TO_ORDER: 'Most Unique to Order',
  RANDOM: 'Random',
  PRIORITY: 'Priority',
};

export const locationTypeTitle: EnumMap<LocationType> = {
  MANAGED: 'Managed',
  STANDARD: 'Standard',
  WAREHOUSE: 'Warehouse',
};

export const shippingMethodStatusTitle: EnumMap<ShippingMethodStatus> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  ARCHIVED: 'Archived',
};
